import { z } from "zod";
/**
 * Validation schemas using Zod.
 * All schemas accept IntlShape for internationalized error messages.
 */
/**
 * One per-authority-type role select (CZNA-1598): unset, one of the NK roles, or the
 * sentinel that leaves a non-standard assignment as it is. Mirrors `NkRoleFormValue`.
 */
var authorityTypeRoleValue = z.union([
    z.literal(""),
    z.literal("CATALOGER"),
    z.literal("LOCAL_SUPERVISOR"),
    z.literal("NK_SUPERVISOR"),
    z.literal("NONSTANDARD"),
]);
/*
 * The map is keyed by authority-type code. CZNA-650 made it a record rather than an object
 * with a fixed key per type: the codes come from the server (record types plus the vocabulary
 * registry), so the schema cannot name them. It still pins every VALUE, and a code the form
 * never rendered simply produces no role mutation (`diffRoles` walks the server list).
 */
/*
 * CZNA-1591 — realm czna's password policy (NK): at least 8 characters, an upper-case
 * letter, a digit, a special character, no letters with diacritics. Keycloak enforces it
 * (keycloak_password_policy in ansible, KEYCLOAK_PASSWORD_POLICY in init-folio.sh) and the
 * backend answers a refusal with 400 {rule}; these rules only let the form say what is
 * missing before the round trip, so they mirror Keycloak's own checks, not a stricter idea:
 *   length        String.length(), i.e. UTF-16 code units — the same as JS `length`
 *   upperCase     Character.isUpperCase — the Unicode Uppercase property
 *   digits        Character.isDigit — Unicode Nd
 *   specialChars  anything that is not Character.isLetterOrDigit (€, §, a space count)
 *   regexPattern  (?:[A-Za-z]|[^\p{L}\p{M}])* over the whole password — an ASCII letter or
 *                 neither a letter nor a combining mark, so č and c + U+030C both fail
 * Order = the order of the hint (users.password.rules); the first rule a password breaks is
 * the one the field names. A changed realm policy needs these and the hint changed with it.
 *
 * The patterns are built with `new RegExp(…, "u")`, never written as `/…/u` literals — and
 * that is load-bearing (as in ui-czna-authorities' searchQueryGuard.ts). Unicode property
 * escapes need the `u` flag, and `stripes build` type-checks this file with
 * stripes-webpack's tsconfig, which has no `target` (ES5), where a `u`-flagged literal is a
 * fatal TS1501 and no bundle is emitted — while this module's own tsconfig says ES2020, so
 * `tsc --noEmit` and jest both pass. A string the RegExp constructor compiles is not parsed
 * by the type-checker, and the flag is fine at runtime (ES2018, every browser Stripes
 * targets). Do not "tidy" these back into literals.
 */
var UPPER_CASE = new RegExp("\\p{Uppercase}", "u");
var DIGIT = new RegExp("\\p{Nd}", "u");
var SPECIAL_CHARACTER = new RegExp("[^\\p{L}\\p{Nd}]", "u");
var NO_DIACRITICS = new RegExp("^(?:[A-Za-z]|[^\\p{L}\\p{M}])*$", "u");
var PASSWORD_RULES = [
    {
        messageId: "ui-czna-users.validation.password.minLength",
        isMet: function (password) { return password.length >= 8; },
    },
    {
        messageId: "ui-czna-users.validation.password.upperCase",
        isMet: function (password) { return UPPER_CASE.test(password); },
    },
    {
        messageId: "ui-czna-users.validation.password.digit",
        isMet: function (password) { return DIGIT.test(password); },
    },
    {
        messageId: "ui-czna-users.validation.password.specialCharacter",
        isMet: function (password) { return SPECIAL_CHARACTER.test(password); },
    },
    {
        messageId: "ui-czna-users.validation.password.noDiacritics",
        isMet: function (password) { return NO_DIACRITICS.test(password); },
    },
];
/** The message id of the first password rule `password` breaks, if any. */
export var firstBrokenPasswordRule = function (password) { var _a; return (_a = PASSWORD_RULES.find(function (rule) { return !rule.isMet(password); })) === null || _a === void 0 ? void 0 : _a.messageId; };
/**
 * Refinement naming the first broken password rule. `optional`: the initial password on the
 * create form may be left empty (the user then gets a welcome e-mail); the set-password
 * dialog's may not, and an empty one breaks the length rule like any short one.
 */
var passwordRules = function (intl, _a) {
    var optional = _a.optional;
    return function (password, ctx) {
        if (optional && !password) {
            return;
        }
        var messageId = firstBrokenPasswordRule(password !== null && password !== void 0 ? password : "");
        if (messageId) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: intl.formatMessage({ id: messageId }),
            });
        }
    };
};
/**
 * Creates a user validation schema (for edit).
 */
export var createUserSchema = function (intl, _a) {
    var _b = _a === void 0 ? {} : _a, isNationalLibrary = _b.isNationalLibrary;
    return z
        .object({
        firstName: z.string().min(1, {
            message: intl.formatMessage({
                id: "ui-czna-users.validation.firstName.required",
            }),
        }),
        lastName: z.string().min(1, {
            message: intl.formatMessage({
                id: "ui-czna-users.validation.lastName.required",
            }),
        }),
        email: z
            .string()
            .min(1, {
            message: intl.formatMessage({
                id: "ui-czna-users.validation.email.required",
            }),
        })
            .email({
            message: intl.formatMessage({
                id: "ui-czna-users.validation.email.invalid",
            }),
        }),
        phone: z.string().optional(),
        // CZNA-1557 — operator acronym for MARC 906 $b. Edges trimmed, case and inner space
        // ("evj e") kept; blank means "no acronym" and is left out of the request.
        acronym: z
            .string()
            .trim()
            .max(20, {
            message: intl.formatMessage({
                id: "ui-czna-users.validation.acronym.maxLength",
            }),
        })
            .optional()
            .transform(function (val) { return val || undefined; }),
        organizationId: z.string().min(1, {
            message: intl.formatMessage({
                id: "ui-czna-users.validation.organizationId.required",
            }),
        }),
        // CZNA-1605: no status — it changes only through Archive / Restore, never the form.
        // Being absent from the schema also strips the loaded user's status from the PUT.
        password: z
            .string()
            .optional()
            .superRefine(passwordRules(intl, { optional: true })),
        passwordConfirm: z.string().optional(),
        temporaryPassword: z.boolean().default(true),
        globalRoles: z.object({
            VOCABULARY_ADMIN: z.boolean(),
            USER_ADMIN: z.boolean(),
            AUTHORITY_ADMIN: z.boolean(),
        }),
        authorityTypeRoles: z.record(z.string(), authorityTypeRoleValue),
    })
        .refine(function (data) { return !data.password || data.password === data.passwordConfirm; }, {
        message: intl.formatMessage({
            id: "ui-czna-users.validation.passwordConfirm.mismatch",
        }),
        path: ["passwordConfirm"],
    })
        .superRefine(function (data, ctx) {
        // CZNA-1598: Supervizor NK is national scope, held only by a user of NK. Kept on the
        // select it concerns, so switching the user to another library with the role still
        // picked blocks the save instead of silently dropping the role.
        if ((isNationalLibrary === null || isNationalLibrary === void 0 ? void 0 : isNationalLibrary(data.organizationId)) !== false)
            return;
        Object.entries(data.authorityTypeRoles).forEach(function (_a) {
            var type = _a[0], value = _a[1];
            if (value === "NK_SUPERVISOR") {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["authorityTypeRoles", type],
                    message: intl.formatMessage({
                        id: "ui-czna-users.validation.nkSupervisor.requiresNationalLibrary",
                    }),
                });
            }
        });
    });
};
/**
 * Creates an organization validation schema.
 */
export var createOrganizationSchema = function (intl) {
    return z.object({
        name: z
            .string()
            .min(2, {
            message: intl.formatMessage({
                id: "ui-czna-users.validation.organization.name.minLength",
            }),
        })
            .max(255, {
            message: intl.formatMessage({
                id: "ui-czna-users.validation.organization.name.maxLength",
            }),
        }),
        sigla: z
            .union([
            z.literal(""),
            z.string().regex(/^[A-Z]{3}\d{3}$/, {
                message: intl.formatMessage({
                    id: "ui-czna-users.validation.organization.sigla.format",
                }),
            }),
        ])
            .optional()
            .transform(function (val) { return val || undefined; }),
        status: z.string().default("ACTIVE"),
        version: z.number().optional(),
    });
};
/**
 * Creates a profile update validation schema (My Profile page).
 */
export var createProfileUpdateSchema = function (intl) {
    return z.object({
        firstName: z.string().min(1, {
            message: intl.formatMessage({
                id: "ui-czna-users.validation.firstName.required",
            }),
        }),
        lastName: z.string().min(1, {
            message: intl.formatMessage({
                id: "ui-czna-users.validation.lastName.required",
            }),
        }),
        phone: z.string().optional(),
    });
};
/**
 * Type inference helpers.
 */
/**
 * Creates a set-password validation schema.
 */
export var createSetPasswordSchema = function (intl) {
    return z
        .object({
        password: z.string().superRefine(passwordRules(intl, { optional: false })),
        passwordConfirm: z.string().min(1, {
            message: intl.formatMessage({
                id: "ui-czna-users.validation.passwordConfirm.required",
            }),
        }),
        temporary: z.boolean().default(true),
    })
        .refine(function (data) { return data.password === data.passwordConfirm; }, {
        message: intl.formatMessage({
            id: "ui-czna-users.validation.passwordConfirm.mismatch",
        }),
        path: ["passwordConfirm"],
    });
};
